import logo from "./logo.svg";
import { SAMText } from "./sections/sam-text/sam-text";
import { SAMLogo } from "./sections/sam-logo/sam-logo";
import SAMIntroduction from "./sections/introduction/sam-introduction";
import ProjectGallery from "./sections/projects/project-gallery";
import { fetchTextes, get, fetchVideos, baseUrl } from "./utils/requests";
import { useEffect, useRef, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";
import Round from "./Round.svg";
import NavBar from "./components/nav-bar/nav-bar";
import ScrollToTopButton from "./components/scroll-to-top";
import StudioCreation from "./sections/studio-creation/studio-creation";
import BreadcrumbSchema from "./components/BreadcrumbSchema";
import { ANIMATION_TIMING, EASING_CURVES } from "./utils/motion";
import StructuredData from "./components/StructuredData";

import WeWork from "./sections/wework/wework";
import { ScrollProvider } from "./utils/ScrollContext";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Gallery from "./components/Gallery";
import Contact from "./components/Contact";
import GridTest from "./components/GridTest";

// Mobile components
import SAMIntroductionMobile from "./sections/introduction/sam-introduction-mobile";
import SAMTextMobile from "./sections/sam-text/sam-text-mobile";
import ProjectGalleryMobile from "./sections/projects/project-gallery-mobile";
import StudioCreationMobile from "./sections/studio-creation/studio-creation-mobile";
import WeWorkMobile from "./sections/wework/wework-mobile";
import SAMLogoMobile from "./sections/sam-logo/sam-logo-mobile";
import NavBarMobile from "./components/nav-bar/nav-bar-mobile";
import CustomFooterMobile from "./components/CustomFooter-mobile";

const MainPage = ({
  textes,
  loading,
  percentage,
  screenSize,
  hideScroll,
  setHideScroll,
  setIsBackgroundWhite,
  isBackgroundWhite,
}) => {
  return (
    <>
      {screenSize >= 768 ? (
        <NavBar textes={textes} isBackgroundWhite={isBackgroundWhite} />
      ) : null}
      <ScrollProvider>
        <div className="snap-proximity ">
          {screenSize >= 768 ? (
            <>
              <SAMIntroduction
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
              />
              <SAMText
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
              />
              <ProjectGallery minimified={false} />
              <StudioCreation
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
              />
              <WeWork
                type="brands"
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
                isBackgroundWhite={isBackgroundWhite}
              />
              <WeWork
                type="agencies"
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
                isBackgroundWhite={isBackgroundWhite}
              />
              <WeWork
                type="associations"
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
                isBackgroundWhite={isBackgroundWhite}
              />
              <SAMLogo
                textes={textes}
                hideScroll={setHideScroll}
                setIsBackgroundWhite={setIsBackgroundWhite}
              />
            </>
          ) : (
            <>
              <NavBarMobile
                textes={textes}
                isBackgroundWhite={isBackgroundWhite}
              />
              <SAMIntroductionMobile textes={textes} />
              <SAMTextMobile textes={textes} />
              <ProjectGalleryMobile minimified={false} />
              <StudioCreationMobile textes={textes} />
              <WeWorkMobile
                type="brands"
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
                isBackgroundWhite={isBackgroundWhite}
              />
              <WeWorkMobile
                type="agencies"
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
                isBackgroundWhite={isBackgroundWhite}
              />
              <WeWorkMobile
                type="associations"
                textes={textes}
                setIsBackgroundWhite={setIsBackgroundWhite}
                isBackgroundWhite={isBackgroundWhite}
              />
              <SAMLogoMobile
                textes={textes}
                hideScroll={setHideScroll}
                setIsBackgroundWhite={setIsBackgroundWhite}
              />
              <CustomFooterMobile
                textes={textes}
                isBackgroundWhite={false}
                minimified
              />
            </>
          )}
        </div>
      </ScrollProvider>
      {screenSize >= 768 ? (
        <ScrollToTopButton isBackgroundWhite={isBackgroundWhite} />
      ) : null}
      {screenSize >= 768 ? (
        <motion.div
          initial={{ rotate: 270, opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 2,
            delay: 1.8,
            repeat: Infinity,
            ease: EASING_CURVES.smooth,
          }}
          className="fixed flex items-center justify-center text-white text-xl xl:text-4xl bottom-[1%] left-[45%] xl:left-[47%] z-[49]"
          style={{
            fontFamily: "Arial Unicode MS",
            fontStretch: "expanded",
            visibility: hideScroll ? "hidden" : "visible",
          }}
        >
          〈〈
        </motion.div>
      ) : null}

      {/* /* Mobile key */}
      {screenSize <= 768 ? (
        <motion.div
          initial={{ y: 0, opacity: 0, rotate: 270 }}
          animate={{
            y: [0, -10, 0], // Petit effet de rebond pour indiquer le scroll
            opacity: hideScroll ? 0 : 1
          }}
          transition={{
            opacity: { duration: 0.5 },
            y: { repeat: Infinity, duration: 2, ease: "easeInOut" }
          }}
          className={`
            sticky z-[999] 
            bottom-12 left-1/2 -translate-x-1/2
            flex items-center justify-center 
            text-white text-2xl
            md:hidden 
            w-2
            ${hideScroll ? "pointer-events-none" : "pointer-events-auto"}
          `}
          style={{
            fontFamily: "Arial Unicode MS",
            visibility: hideScroll ? "hidden" : "visible",

          }}
        >
          〈〈
        </motion.div>
      ) : null}
    </>
  );
};

function App() {
  // flashlight cursor
  const [textes, setTextes] = useState();
  const [videosProp, setVideosProp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [percentage, setPercentage] = useState(0);
  const [screenSize, setScreenSize] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [hideScroll, setHideScroll] = useState(false);
  const [isBackgroundWhite, setIsBackgroundWhite] = useState(false);

  const getTextes = async () => {
    const result = fetchTextes();
    const videos = fetchVideos();

    result.then((results) => {
      const tmpTextes = results.data.reduce((acc, texte) => {
        switch (texte.attributes.name) {
          case "intro1":
            return { ...acc, intro1: texte.attributes?.value };
          case "intro2":
            return { ...acc, intro2: texte.attributes.value };
          case "mentra1":
            return { ...acc, mentra1: texte.attributes.value };
          case "mentra2":
            return { ...acc, mentra2: texte.attributes.value };
          case "mentra3":
            return { ...acc, mentra3: texte.attributes.value };
          case "wework-brands":
            return { ...acc, weworkBrands: texte.attributes.value };
          case "wework-agencies":
            return { ...acc, weworkAgencies: texte.attributes.value };
          case "wework-associations":
            return { ...acc, weworkAssociations: texte.attributes.value };
          case "text-scrolling":
            return { ...acc, textScrolling: texte.attributes.value };
          case "stay-connected":
            return { ...acc, stayConnected: texte.attributes.value };
          case "contact-email":
            return { ...acc, contactEmail: texte.attributes.value };
          case "all-rights-reserved":
            return { ...acc, allRightsReserved: texte.attributes.value };
          case "instagram-link":
            return { ...acc, instagramLink: texte.attributes.value };
          case "linkedin-link":
            return { ...acc, linkedinLink: texte.attributes.value };
          case "facebook-link":
            return { ...acc, facebookLink: texte.attributes.value };
          case "random-words":
            return { ...acc, randomWords: texte.attributes.value };
          case "meta-title":
            return { ...acc, metaTitle: texte.attributes.value };
          case "meta-description":
            return { ...acc, metaDescription: texte.attributes.value };
          case "meta-tags":
            return { ...acc, metaTags: texte.attributes.value };
          case "meta-author":
            return { ...acc, metaAuthor: texte.attributes.value };
          case "loading-screen":
            return { ...acc, loadingScreen: texte.attributes.value };
          case "not-for-mobile":
            return { ...acc, NotForMobile: texte.attributes.value };
          case "nav-bar-item-1":
            return {
              ...acc,
              navBarItem1: texte.attributes.value.toLowerCase(),
            };
          case "nav-bar-item-2":
            return {
              ...acc,
              navBarItem2: texte.attributes.value.toLowerCase(),
            };
          case "studio-creation-title":
            return { ...acc, studioCreationTitle: texte.attributes.value };
          case "studio-creation-long-description":
            return {
              ...acc,
              studioCreationLongDescription: texte.attributes.value,
            };
          case "available-soon":
            return { ...acc, availableSoon: texte.attributes.value };
          default:
            return acc;
        }
      }, {});
      setTextes(tmpTextes);
    });

    videos.then((videoResults) => {
      const videosFinal = videoResults.map(function (video) {

        let videoFormat = "";

        if (video.name.includes("9x16")) {
          videoFormat = "9:16";
        }
        else if (video.name.includes("4x5")) {
          videoFormat = "4:5";
        }
        else if (video.name.includes("16x9")) {
          videoFormat = "16:9";
        }
        return {
          url: baseUrl.split("/api/")[0] + video.url,
          aspectRatio: videoFormat,
        }
      });
      setVideosProp(videosFinal);
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setPercentage((prevPercentage) => {
        const nextPercentage = prevPercentage + 1;
        return nextPercentage > 100 ? 100 : nextPercentage;
      });
    }, 10);

    setTimeout(() => {
      clearInterval(interval);
      setPercentage(100);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }, 1000);

    return () => clearInterval(interval);
  }, [textes, videosProp]);

  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };

    if (textes === undefined) getTextes();

    document.addEventListener("mousemove", update);
    document.addEventListener("touchmove", update);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousemove", update);
      document.removeEventListener("touchmove", update);
    };
  }, []);

  const update = (e) => {
    var x = e?.clientX === 0 ? 1 : e?.clientX || e?.touches[0]?.clientX;
    var y = e?.clientY === 0 ? 1 : e?.clientY || e?.touches[0]?.clientY;

    requestAnimationFrame(() => {
      document.documentElement.style.setProperty("--cursorX", x + "px");
      document.documentElement.style.setProperty("--cursorY", y + "px");
    });
  };

  return (
    <HelmetProvider>
      {textes?.metaDescription && (
        <>
          <Helmet>
            {/* Primary Meta Tags */}
            <html lang="fr" />
            <title>
              {textes?.metaTitle || "Studio SAM - Studio animé et mouvementé"}
            </title>
            <meta
              name="title"
              content={
                textes?.metaTitle || "Studio SAM - Studio animé et mouvementé"
              }
            />
            <meta
              name="description"
              content={
                textes?.metaDescription ||
                "Studio de création audiovisuelle spécialisé dans l'animation et le motion design. Découvrez nos projets pour marques, agences et associations."
              }
            />
            <meta
              name="keywords"
              content={
                textes?.metaTags ||
                "motion design, animation, vidéo, création audiovisuelle, studio créatif, agence motion design"
              }
            />
            <meta name="author" content={textes?.metaAuthor || "Studio SAM"} />

            {/* Canonical URL */}
            <link rel="canonical" href="https://studio-sam.fr" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://studio-sam.fr" />
            <meta
              property="og:title"
              content={
                textes?.metaTitle || "Studio SAM - Studio animé et mouvementé"
              }
            />
            <meta
              property="og:description"
              content={
                textes?.metaDescription ||
                "Studio de création audiovisuelle spécialisé dans l'animation et le motion design"
              }
            />
            <meta
              property="og:image"
              content="https://studio-sam.fr/favicon.ico"
            />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="627" />
            <meta property="og:site_name" content="Studio SAM" />
            <meta property="og:locale" content="fr_FR" />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content="https://studio-sam.fr" />
            <meta
              property="twitter:title"
              content={
                textes?.metaTitle || "Studio SAM - Studio animé et mouvementé"
              }
            />
            <meta
              property="twitter:description"
              content={
                textes?.metaDescription ||
                "Studio de création audiovisuelle spécialisé dans l'animation et le motion design"
              }
            />
            <meta
              property="twitter:image"
              content="https://studio-sam.fr/favicon.ico"
            />

            {/* Additional SEO Meta Tags */}
            <meta
              name="robots"
              content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
            />
            <meta name="googlebot" content="index, follow" />
            <meta name="format-detection" content="telephone=no" />
            <meta httpEquiv="x-ua-compatible" content="ie=edge" />
          </Helmet>

          {/* Structured Data */}
          <StructuredData />
        </>
      )}

      {textes && <BreadcrumbSchema textes={textes} />}

      <BrowserRouter>
        <div className="bg-sam-black">
          {loading && (
            <>
              <motion.div
                className="relative bg-sam-black w-screen h-screen flex flex-col items-center justify-center"
                animate={{ opacity: percentage < 100 ? 1 : 0.1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.5,
                  ease: EASING_CURVES.smooth,
                }}
              >
                <motion.img
                  src={Round}
                  className="w-[450px]"
                  animate={{ rotate: 360, scale: percentage / 100 }}
                  transition={{
                    repeat: Infinity,
                    duration: 6,
                    ease: EASING_CURVES.smooth,
                  }}
                  alt="logo-round"
                />
                <motion.div
                  className="text-white text-7xl sm:text-8xl lg:text-9xl text-center font-lexend absolute"
                  animate={{ scale: 0.8 }}
                  transition={{
                    duration: 3,
                    ease: EASING_CURVES.smooth,
                  }}
                >
                  {percentage + "%"}
                </motion.div>
              </motion.div>
            </>
          )}

          {loading === false && textes && (
            <Routes>
              <Route
                path="/"
                element={
                  <MainPage
                    textes={textes}
                    loading={loading}
                    percentage={percentage}
                    screenSize={screenSize}
                    hideScroll={hideScroll}
                    setHideScroll={setHideScroll}
                    setIsBackgroundWhite={setIsBackgroundWhite}
                    isBackgroundWhite={isBackgroundWhite}
                  />
                }
              />
              {videosProp && (
                <Route
                  path="/gallery"
                  element={
                    <Gallery
                      textes={textes}
                      screenSize={screenSize}
                      videosProps={videosProp}
                    />
                  }
                />
              )}

              <Route
                path="/contact"
                element={
                  <Contact
                    textes={textes}
                    screenSize={screenSize}
                    setIsBackgroundWhite={setIsBackgroundWhite}
                    isBackgroundWhite={isBackgroundWhite}
                  />
                }
              />

              <Route
                path="/grid-test"
                element={<GridTest />}
              />
            </Routes>
          )}
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
